import React, { useState, useEffect } from 'react'
import { string, bool, func } from 'prop-types'
import { emailSignup, setReset } from '@redux/modules/email-subscribe'
import { useSelector, useDispatch } from 'react-redux'
import { validateEmail, validateZip } from '@helpers/string-helper'
import { useInjectSaga } from '@redux/injectSaga'
import { useNotifications } from '@context/notifications'
import { styled, TextField, css, useMediaQuery } from '@mui/material'
import { useTest } from '@hooks/useTest'
import saga from '@redux/coreSaga'
import loaderLight from '../../assets/images/loader-light.svg'

export const generateErrorMessage = (isEmailValid, isZipValid) => {
  if (!isEmailValid) {
    return { message: '*Invalid email address.', id: 'email-error' }
  }
  if (!isZipValid) {
    return { message: '*Invalid zip code', id: 'zip-error' }
  }
  return {}
}

const StyledInput = styled(TextField)(
  ({ theme }) => css`
    background-color: ${theme.palette.common.white};
    border-radius: 4px;

    .MuiInputBase-root {
      margin: 0 !important;

      &::before,
      &::after {
        border: none;
        border-bottom: none !important;
      }

      &:hover {
        &::before,
        &::after {
          border: none;
          border-bottom: none !important;
        }
      }
    }

    .MuiInputBase-input {
      font-size: 16px;
      line-height: initial;
      padding: 7.5px 12px !important;
      border-radius: inherit;
      border-radius: 4px;
    }

    .Mui-error .MuiInputBase-input {
      padding: 6.5px 12px !important;
      border: 1px solid ${theme.palette.error.border} !important;
    }

    .MuiInputLabel-root {
      transform: translate(12px, 8px) scale(1);
      color: ${theme.palette.secondary.dark};

      &.Mui-focused,
      &.MuiFormLabel-filled {
        transform: translate(4px, -16px) scale(0.75);
        text-transform: uppercase;
        color: ${theme.palette.common.white};
        font-weight: 600;
      }
    }
  `,
)

export const EmailSubscribeForm = ({
  className,
  supressZipCode,
  supressInfoMessage,
  onSuccessCallback,
  subsourcecode,
}) => {
  useInjectSaga({ key: 'email-subscribe', saga, keepAlive: true })
  const { generateTestId } = useTest()
  const error = useSelector(state => state.emailSubscribe.error)
  const success = useSelector(state => state.emailSubscribe.success)
  const loading = useSelector(state => state.emailSubscribe.loading)
  const [isEmailValid, setEmailValid] = useState(true)
  const [isZipValid, setZipValid] = useState(true)
  const { enqueue } = useNotifications()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'))

  const [email, setEmail] = useState('')
  const [zipCode, setZipCode] = useState('')

  const onFormSubmit = event => {
    if (event && event.preventDefault) event.preventDefault()
    const validEmail = validateEmail(email)
    const validZip = !supressZipCode ? validateZip(zipCode) : true
    setEmailValid(validEmail)
    setZipValid(validZip)
    if (validEmail && validZip) {
      dispatch(emailSignup(email, zipCode, subsourcecode))
      setupAnalytics(email)
      setEmail('')
      setZipCode('')
    }
  }

  const setupAnalytics = uEmail => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'footer_email_sign-up', user: { email: uEmail } })
    }
  }

  const info = {
    email,
    zip: zipCode,
  }

  useEffect(() => {
    if (success) {
      if (typeof onSuccessCallback === 'function') {
        onSuccessCallback?.()
      } else {
        enqueue({ variant: 'filled', body: 'Signed up for email successfully!' })
      }
    }
    return () => dispatch(setReset())
  }, [dispatch, enqueue, onSuccessCallback, success])

  const invalidFields = []
  if (!isEmailValid) invalidFields.push('email')
  if (!isZipValid) invalidFields.push('zip')
  const { message, id } = generateErrorMessage(isEmailValid, isZipValid)
  return (
    <div
      className={`footer__email-subscribe-form ${className}`}
      data-testid={generateTestId('footer', 'email-subscribe-wrapper')}
    >
      {!supressInfoMessage && <span className="footer__email-subscribe-form__title">Sign Up</span>}
      <fieldset>
        {!supressInfoMessage && (
          <legend className="footer__email-subscribe-form__label">
            {`Enter your email ${!supressZipCode ? 'and ZIP ' : ''}code to receive special offers and more!`}
          </legend>
        )}
        <form
          className="footer__email-subscribe-form__form"
          data-testid={generateTestId('footer', 'email-subscribe-form')}
          onSubmit={e => {
            e.preventDefault()
          }}
        >
          {error && <p className="error">*Unable to sign up for emails.</p>}
          <div
            className={`footer__email-subscribe-form__field-container ${
              !supressZipCode || isMobile ? 'full-line' : ''
            }`}
          >
            <StyledInput
              value={email}
              onChange={e => setEmail(e.target.value)}
              label="Email Address"
              size="small"
              variant="standard"
              autoComplete="email"
              type="email"
              id="signup_email"
              error={invalidFields?.includes('email')}
            />
            {message && id === 'email-error' && (
              <p
                className="footer__email-subscribe-form__field-error"
                data-testid={generateTestId('footer', 'email-subscribe-email-error')}
              >
                {message}
              </p>
            )}
          </div>
          {!supressZipCode && (
            <div className="footer__email-subscribe-form__field-container">
              <StyledInput
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                label="ZIP Code"
                size="small"
                variant="standard"
                autoComplete="postal-code"
                id="signup_zip"
                error={invalidFields?.includes('zip')}
              />
              {message && id === 'zip-error' && <p className="footer__email-subscribe-form__field-error">{message}</p>}
            </div>
          )}
          <button
            type="button"
            value="Sign Up"
            name="signup_btn"
            className="footer__email-subscribe-form__signup-btn"
            id="signup_btn"
            gtm-category="footer"
            gtm-action="email signup"
            onClick={onFormSubmit}
          >
            {loading ? <img className="loader" alt="Sign Up" src={loaderLight} /> : 'Sign Up'}
          </button>
        </form>
      </fieldset>
    </div>
  )
}

EmailSubscribeForm.propTypes = {
  className: string,
  supressZipCode: bool,
  onSuccessCallback: func,
  subsourcecode: string,
  supressInfoMessage: bool,
}

export default EmailSubscribeForm
